import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  CircularProgress,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Alert,
  Stack,
  Checkbox,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  UserObjectDimension,
  type ClaimDetailDto,
  type ClaimSummaryDto,
  type ConflictViewItemDto,
  type ConversationMemoryViewDto,
  type DimensionViewDto,
  type ProfileSnapshotDto,
  type ReadPathLensDto,
} from '@common/synapse/introspection-types';
import { useUserInfoContext } from '../../lib/contexts/UserInfoContext';
import {
  fetchClaimDetail,
  fetchConflicts,
  fetchConversationMemoryView,
  fetchDimensionView,
  fetchProfileSnapshot,
  fetchReadPathLens,
  fetchSynapseSessions,
  fetchSynapseUsers,
  bulkRetractClaims,
  type SynapseIntrospectionRequestOptions,
  type SynapseSessionSummary,
  type SynapseSubjectUser,
} from '../../lib/api/synapseIntrospection';
import ClaimDetailDrawer from './ClaimDetailDrawer';
import ReadPathLensPanel from './ReadPathLensPanel';
import { AddManualClaimDialog } from './ClaimCurationPanel';

function StrengthDots({ filled, total = 6 }: { filled: number; total?: number }) {
  return (
    <Box component="span" sx={{ fontFamily: 'monospace', letterSpacing: 2 }}>
      {'●'.repeat(filled)}
      {'○'.repeat(Math.max(0, total - filled))}
    </Box>
  );
}

function ProvenanceChip({ provenance }: { provenance: ClaimSummaryDto['provenance'] }) {
  const color =
    provenance === 'manual' ? 'secondary' : provenance === 'edited' ? 'warning' : provenance === 'retracted' ? 'default' : 'success';
  return <Chip size="small" label={provenance} color={color} variant="outlined" />;
}

const CONFLICT_TYPE_LABELS: Record<string, string> = {
  explicit_replacement: 'Explicit replacement',
  temporal_update: 'Temporal update',
  contextual_coexistence: 'Contextual coexistence',
  extraction_ambiguity: 'Extraction ambiguity',
  likely_misattribution: 'Likely misattribution',
  no_conflict: 'No conflict',
};

const RESOLUTION_ACTION_LABELS: Record<string, string> = {
  SUPERSEDE_OLD: 'Supersede existing claim',
  COEXIST_WITH_REKEY: 'Coexist with rekey',
  MERGE: 'Merge claims',
  DISCARD_CANDIDATE: 'Discard candidate',
  ESCALATE_REVIEW: 'Escalated for manual review',
};

function sortSessionsByRecency(sessions: SynapseSessionSummary[]): SynapseSessionSummary[] {
  return [...sessions].sort(
    (a, b) => new Date(b.updatedAt ?? 0).getTime() - new Date(a.updatedAt ?? 0).getTime()
  );
}

function ConflictClaimPanel({
  label,
  claim,
  evidence,
  sourceTurnId,
  onOpenClaim,
}: {
  label: string;
  claim: ClaimSummaryDto | null;
  evidence: string[];
  sourceTurnId: string | null;
  onOpenClaim: (claimId: string) => void;
}) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {claim ? (
        <Box sx={{ mt: 0.5 }}>
          <Typography
            variant="body2"
            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            onClick={() => onOpenClaim(claim.id)}
          >
            {claim.predicate}="{claim.value}"
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
            <Chip size="small" label={`conf ${claim.confidence.toFixed(2)}`} />
            <ProvenanceChip provenance={claim.provenance} />
            {claim.lastSeenTurn != null && <Chip size="small" variant="outlined" label={`turn ${claim.lastSeenTurn}`} />}
          </Stack>
          {sourceTurnId && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Source turn: {sourceTurnId}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5, mb: 0.5 }}>
            Evidence
          </Typography>
          {evidence.length > 0 ? (
            evidence.map((quote, i) => (
              <Typography key={i} variant="body2" sx={{ fontStyle: 'italic', mb: 0.75, pl: 1, borderLeft: 2, borderColor: 'divider' }}>
                "{quote}"
              </Typography>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No evidence span recorded
            </Typography>
          )}
        </Box>
      ) : (
        <Typography variant="body2">—</Typography>
      )}
    </Box>
  );
}

export interface SynapseIntrospectionTabProps {
  /** When true, enables client-demo presentation (PII redaction, no editing, no pipeline detail). */
  clientDemoMode?: boolean;
  /** When true, hides the engineering/demo toggle (e.g. dedicated demo URL). */
  forceClientDemoMode?: boolean;
}

const SynapseIntrospectionTab = ({
  clientDemoMode: clientDemoModeProp = false,
  forceClientDemoMode = false,
}: SynapseIntrospectionTabProps) => {
  const { userInfo } = useUserInfoContext();
  const token = userInfo?.accessToken;

  const [clientDemoMode, setClientDemoMode] = useState(clientDemoModeProp || forceClientDemoMode);

  const [users, setUsers] = useState<SynapseSubjectUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [mainTab, setMainTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [snapshot, setSnapshot] = useState<ProfileSnapshotDto | null>(null);
  const [dimension, setDimension] = useState<UserObjectDimension | null>(null);
  const [dimensionView, setDimensionView] = useState<DimensionViewDto | null>(null);
  const [sessions, setSessions] = useState<SynapseSessionSummary[]>([]);
  const [sessionsRefreshing, setSessionsRefreshing] = useState(false);
  const [selectedChatId, setSelectedChatId] = useState('');
  const [conversation, setConversation] = useState<ConversationMemoryViewDto | null>(null);
  const [conflicts, setConflicts] = useState<ConflictViewItemDto[]>([]);

  const [claimDetail, setClaimDetail] = useState<ClaimDetailDto | null>(null);
  const [claimDrawerOpen, setClaimDrawerOpen] = useState(false);
  const [expandedPredicates, setExpandedPredicates] = useState<Set<string>>(new Set());
  const [selectedClaimIds, setSelectedClaimIds] = useState<Set<string>>(new Set());
  const [bulkRetractOpen, setBulkRetractOpen] = useState(false);
  const [bulkRetractReason, setBulkRetractReason] = useState<'misattribution' | 'low_quality' | 'privacy_concern' | 'study_protocol_exclusion' | 'other'>('study_protocol_exclusion');
  const [addClaimOpen, setAddClaimOpen] = useState(false);
  const [conversationRevision, setConversationRevision] = useState(0);
  const [readPathOpen, setReadPathOpen] = useState(false);
  const [readPathLoading, setReadPathLoading] = useState(false);
  const [readPathError, setReadPathError] = useState<string | null>(null);
  const [readPathLens, setReadPathLens] = useState<ReadPathLensDto | null>(null);
  const [readPathTurnIndex, setReadPathTurnIndex] = useState<number | null>(null);
  const sessionsLengthRef = useRef(0);

  useEffect(() => {
    sessionsLengthRef.current = sessions.length;
  }, [sessions.length]);

  useEffect(() => {
    setClientDemoMode(clientDemoModeProp || forceClientDemoMode);
  }, [clientDemoModeProp, forceClientDemoMode]);

  const subjectIndex = users.findIndex((user) => user.userId === selectedUserId);
  const requestOptions = useMemo<SynapseIntrospectionRequestOptions>(
    () => ({
      clientDemoMode,
      subjectIndex: subjectIndex >= 0 ? subjectIndex : undefined,
    }),
    [clientDemoMode, subjectIndex]
  );

  const formatSubjectLabel = (user: SynapseSubjectUser, index: number) => {
    if (clientDemoMode) {
      return user.displayLabel ?? `Participant ${index + 1}`;
    }
    return `${user.email ?? user.userId}${user.inCognito === false ? ' (Qdrant only)' : ''}${
      user.hasMemory ? ` · ${user.activeClaimCount} claims` : ' · no memory'
    }`;
  };

  useEffect(() => {
    setUsersLoading(true);
    fetchSynapseUsers(token, requestOptions)
      .then(setUsers)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load users'))
      .finally(() => setUsersLoading(false));
  }, [token, clientDemoMode]);

  useEffect(() => {
    if (usersLoading || selectedUserId || !userInfo?.userId) return;
    if (users.some((user) => user.userId === userInfo.userId)) {
      setSelectedUserId(userInfo.userId);
    }
  }, [usersLoading, users, selectedUserId, userInfo?.userId]);

  const loadUserData = useCallback(async (userId: string) => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    setConversation(null);
    setSelectedChatId('');
    setSessions([]);
    try {
      const [snap, sess, conf] = await Promise.all([
        fetchProfileSnapshot(userId, token, requestOptions),
        fetchSynapseSessions(userId, token, requestOptions),
        clientDemoMode ? Promise.resolve({ conflicts: [] }) : fetchConflicts(userId, token, requestOptions),
      ]);
      setSnapshot(snap);
      setSessions(sortSessionsByRecency(sess.sessions ?? []));
      setConflicts(conf.conflicts ?? []);
      setDimension(null);
      setDimensionView(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load user data');
    } finally {
      setLoading(false);
    }
  }, [token, clientDemoMode, requestOptions]);

  useEffect(() => {
    if (selectedUserId) loadUserData(selectedUserId);
  }, [selectedUserId, loadUserData]);

  useEffect(() => {
    if (clientDemoMode && selectedUserId) {
      setMainTab(2);
    }
  }, [clientDemoMode, selectedUserId]);

  const openDimension = async (dim: UserObjectDimension) => {
    if (!selectedUserId) return;
    setDimension(dim);
    setMainTab(1);
    setLoading(true);
    try {
      const view = await fetchDimensionView(selectedUserId, dim, token, requestOptions);
      setDimensionView(view);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load dimension');
    } finally {
      setLoading(false);
    }
  };

  const openClaim = async (claimId: string) => {
    if (!selectedUserId) return;
    setClaimDrawerOpen(true);
    setClaimDetail(null);
    try {
      const detail = await fetchClaimDetail(selectedUserId, claimId, token, requestOptions);
      setClaimDetail(detail);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load claim');
    }
  };

  const refreshClaimDetail = useCallback(async () => {
    if (!selectedUserId || !claimDetail?.claim.id) return;
    try {
      const detail = await fetchClaimDetail(selectedUserId, claimDetail.claim.id, token, requestOptions);
      setClaimDetail(detail);
      if (dimension) {
        const view = await fetchDimensionView(selectedUserId, dimension, token, requestOptions);
        setDimensionView(view);
      }
      const snap = await fetchProfileSnapshot(selectedUserId, token, requestOptions);
      setSnapshot(snap);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to refresh claim');
    }
  }, [selectedUserId, claimDetail?.claim.id, token, dimension, requestOptions]);

  const loadConversation = useCallback(
    async (chatId: string) => {
      if (!selectedUserId || !chatId) return;
      setSelectedChatId(chatId);
      setLoading(true);
      setConversation(null);
      try {
        const view = await fetchConversationMemoryView(selectedUserId, chatId, token, requestOptions);
        setConversation(view);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load conversation');
      } finally {
        setLoading(false);
      }
    },
    [selectedUserId, token, requestOptions]
  );

  const refreshSessions = useCallback(
    async (options?: { selectNewest?: boolean }) => {
      if (!selectedUserId) return;
      setSessionsRefreshing(true);
      setError(null);
      try {
        const [sess, snap] = await Promise.all([
          fetchSynapseSessions(selectedUserId, token, requestOptions),
          fetchProfileSnapshot(selectedUserId, token, requestOptions),
        ]);
        const nextSessions = sortSessionsByRecency(sess.sessions ?? []);
        setSessions(nextSessions);
        setSnapshot(snap);

        setSelectedChatId((prev) => {
          const hasNewSessions = nextSessions.length > sessionsLengthRef.current;
          const prevStillExists = prev && nextSessions.some((session) => session.chatId === prev);
          if (options?.selectNewest || hasNewSessions || !prevStillExists) {
            return nextSessions[0]?.chatId ?? '';
          }
          return prev;
        });
        setConversationRevision((revision) => revision + 1);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to refresh sessions');
      } finally {
        setSessionsRefreshing(false);
      }
    },
    [selectedUserId, token, requestOptions]
  );

  const viewClaimInConversation = useCallback(
    (chatId: string) => {
      setMainTab(2);
      void loadConversation(chatId);
    },
    [loadConversation]
  );

  const openReadPathLens = useCallback(
    (turnId: string, turnIndex: number) => {
      if (!selectedUserId || !selectedChatId) {
        return;
      }
      setReadPathOpen(true);
      setReadPathTurnIndex(turnIndex);
      setReadPathLoading(true);
      setReadPathError(null);
      setReadPathLens(null);
      void fetchReadPathLens(selectedUserId, selectedChatId, turnId, token, requestOptions)
        .then(setReadPathLens)
        .catch((e: unknown) => setReadPathError(e instanceof Error ? e.message : 'Failed to load read-path lens'))
        .finally(() => setReadPathLoading(false));
    },
    [selectedUserId, selectedChatId, token, requestOptions]
  );

  const canOpenReadPathLens = (turn: ConversationMemoryViewDto['turns'][number]) =>
    Boolean(turn.assistantText.trim()) &&
    (turn.useSynapse || turn.hasReadPathSnapshot || turn.memoryEvents.length > 0);

  useEffect(() => {
    if (mainTab !== 2 || !selectedUserId || sessions.length === 0) return;
    const chatId = selectedChatId || sessions[0]!.chatId;
    if (!chatId) return;
    void loadConversation(chatId);
  }, [mainTab, selectedUserId, sessions, selectedChatId, conversationRevision, loadConversation]);

  useEffect(() => {
    if (mainTab !== 2 || !selectedUserId) return;
    void refreshSessions();
  }, [mainTab, selectedUserId, refreshSessions]);

  const handleMainTabChange = (_: unknown, value: number) => {
    setMainTab(value);
  };

  const formatSessionLabel = (title: string, exchangeCount: number) =>
    `${title.replace(/^Synthetic Session:\s*/i, '')} (${exchangeCount} turn${exchangeCount === 1 ? '' : 's'})`;

  const togglePredicate = (predicate: string) => {
    setExpandedPredicates((prev) => {
      const next = new Set(prev);
      if (next.has(predicate)) next.delete(predicate);
      else next.add(predicate);
      return next;
    });
  };

  const renderClaimRow = (claim: ClaimSummaryDto, selectable = false) => (
    <ListItemButton
      key={claim.id}
      onClick={() => openClaim(claim.id)}
      sx={{ pl: selectable && !clientDemoMode ? 2 : 4, alignItems: 'center', py: 1 }}
    >
      {selectable && !clientDemoMode && (
        <Checkbox
          size="small"
          checked={selectedClaimIds.has(claim.id)}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            e.stopPropagation();
            setSelectedClaimIds((prev) => {
              const next = new Set(prev);
              if (next.has(claim.id)) next.delete(claim.id);
              else next.add(claim.id);
              return next;
            });
          }}
          sx={{ p: 0.5, mr: 0.5, flexShrink: 0 }}
        />
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" component="div" sx={{ lineHeight: 1.35 }}>
          {claim.predicate}="{claim.value}"
        </Typography>
        <Typography variant="caption" component="div" color="text.secondary" sx={{ lineHeight: 1.2, mt: 0.25 }}>
          conf {claim.confidence.toFixed(2)}
          {claim.lastSeenTurn != null ? ` · turn ${claim.lastSeenTurn}` : ''}
        </Typography>
      </Box>
      <Box sx={{ flexShrink: 0, ml: 1, display: 'flex', alignItems: 'center' }}>
        <ProvenanceChip provenance={claim.provenance} />
      </Box>
    </ListItemButton>
  );

  return (
    <Box
      sx={{
        p: clientDemoMode ? 3 : 2,
        height: '100%',
        overflow: 'auto',
        bgcolor: clientDemoMode ? 'grey.50' : 'background.default',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant={clientDemoMode ? 'h3' : 'h4'} gutterBottom sx={{ mb: 0 }}>
          {clientDemoMode ? 'User Profile' : 'User Object Inspector'}
        </Typography>
        {!forceClientDemoMode && (
          <FormControlLabel
            control={
              <Switch
                checked={clientDemoMode}
                onChange={(event) => setClientDemoMode(event.target.checked)}
                color="primary"
              />
            }
            label="Client demo mode"
          />
        )}
      </Stack>

      {clientDemoMode && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Client-demo view: conversation text is PII-redacted server-side. Editing and engineering pipeline details are hidden.
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, maxWidth: 480 }}>
        <FormControl fullWidth disabled={usersLoading}>
          <InputLabel id="synapse-subject-select-label">Subject</InputLabel>
          <Select
            labelId="synapse-subject-select-label"
            label="Subject"
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
          >
            <MenuItem value="">
              {usersLoading ? 'Loading users…' : 'Select a user…'}
            </MenuItem>
            {users.map((u, index) => (
              <MenuItem key={u.userId} value={u.userId}>
                {formatSubjectLabel(u, index)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {usersLoading && <CircularProgress size={24} aria-label="Loading users" />}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!usersLoading && !selectedUserId && (
        <Alert severity="info">Select a user to inspect their User Object.</Alert>
      )}

      {selectedUserId && loading && !snapshot && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {snapshot && (
        <>
          <Paper
            variant="outlined"
            sx={{
              p: clientDemoMode ? 3 : 2,
              mb: 2,
              borderRadius: clientDemoMode ? 2 : 1,
              boxShadow: clientDemoMode ? 1 : 0,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              {clientDemoMode
                ? `Sessions: ${snapshot.sessionCount} · Turns: ${snapshot.turnCount}`
                : `Subject: ${selectedUserId.slice(0, 12)}… · Sessions: ${snapshot.sessionCount} · Turns: ${snapshot.turnCount}`}
            </Typography>
            <Typography variant="h6" sx={{ mt: 1 }}>
              Profile maturity: {snapshot.profileMaturity ?? 'unknown'}
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {snapshot.dimensions.map((d) => (
                <Grid item xs={12} sm={6} key={d.dimension}>
                  <Box
                    component="button"
                    onClick={() => openDimension(d.dimension)}
                    sx={{
                      width: '100%',
                      textAlign: 'left',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 1.5,
                      cursor: 'pointer',
                      bgcolor: 'background.paper',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Typography variant="overline">{d.label}</Typography>
                    <Box>
                      <StrengthDots filled={d.strengthDots} /> {d.strongClaimCount} strong claims
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
            {snapshot.topSignals.length > 0 && (
              <Typography variant="body2" sx={{ mt: 2 }}>
                <strong>Top signals:</strong> {snapshot.topSignals.join(' · ')}
              </Typography>
            )}
          </Paper>

          <Tabs value={mainTab} onChange={handleMainTabChange} sx={{ mb: mainTab === 2 ? 0 : 2 }}>
            <Tab label="Overview" />
            <Tab label="Dimension" disabled={!dimension} />
            <Tab label="Conversation" />
            {!clientDemoMode && <Tab label={`Conflicts (${conflicts.length})`} />}
          </Tabs>

          {mainTab === 2 && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
              {sessions.length === 0 ? (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Alert severity="info" sx={{ py: 0.5, flex: 1 }}>
                    No chat sessions for this user. Chat in the app, then refresh here.
                  </Alert>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={sessionsRefreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
                    onClick={() => void refreshSessions({ selectNewest: true })}
                    disabled={sessionsRefreshing}
                  >
                    Refresh
                  </Button>
                </Stack>
              ) : (
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FormControl fullWidth size="small">
                      <InputLabel id="synapse-session-select">Session</InputLabel>
                      <Select
                        labelId="synapse-session-select"
                        label="Session"
                        value={selectedChatId}
                        onChange={(e) => void loadConversation(e.target.value)}
                      >
                        {sessions.map((s) => (
                          <MenuItem key={s.chatId} value={s.chatId}>
                            {formatSessionLabel(s.title, s.exchangeCount)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={sessionsRefreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
                      onClick={() => void refreshSessions()}
                      disabled={sessionsRefreshing}
                      sx={{ flexShrink: 0 }}
                    >
                      Refresh
                    </Button>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {sessions.length} session{sessions.length === 1 ? '' : 's'} loaded
                    {snapshot && snapshot.sessionCount !== sessions.length
                      ? ` (snapshot reports ${snapshot.sessionCount})`
                      : ''}
                    . Sessions reload when you open this tab; use Refresh after new chats.
                  </Typography>
                </Stack>
              )}
            </Paper>
          )}

          {mainTab === 0 && (
            <Typography variant="body2" color="text.secondary">
              Click a dimension card above to explore claims by predicate, or open the Conversation tab to see memory events inline with chat.
            </Typography>
          )}

          {mainTab === 1 && dimensionView && (
            <Box>
              {!clientDemoMode && (
                <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                  <Button size="small" variant="outlined" onClick={() => setAddClaimOpen(true)}>
                    Add manual claim
                  </Button>
                  {selectedClaimIds.size > 0 && (
                    <Button size="small" variant="outlined" color="warning" onClick={() => setBulkRetractOpen(true)}>
                      Retract selected ({selectedClaimIds.size})
                    </Button>
                  )}
                </Stack>
              )}
              <Typography variant="h6">{dimensionView.label}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {dimensionView.description}
              </Typography>
              {dimensionView.activeByPredicate.map((group) => (
                <Paper key={group.predicate} variant="outlined" sx={{ mb: 1 }}>
                  <ListItemButton onClick={() => togglePredicate(group.predicate)}>
                    <ListItemText primary={group.predicate} secondary={`${group.claims.length} claim(s)`} />
                    {expandedPredicates.has(group.predicate) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </ListItemButton>
                  <Collapse in={expandedPredicates.has(group.predicate)}>
                    <List dense>{group.claims.map((c) => renderClaimRow(c, !clientDemoMode))}</List>
                  </Collapse>
                </Paper>
              ))}
              {dimensionView.pendingByPredicate.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                    Pending & superseded
                  </Typography>
                  {dimensionView.pendingByPredicate.map((group) => (
                    <Paper key={`pending-${group.predicate}`} variant="outlined" sx={{ mb: 1, opacity: 0.85 }}>
                      <ListItemButton onClick={() => togglePredicate(`pending-${group.predicate}`)}>
                        <ListItemText primary={group.predicate} />
                        {expandedPredicates.has(`pending-${group.predicate}`) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </ListItemButton>
                      <Collapse in={expandedPredicates.has(`pending-${group.predicate}`)}>
                        <List dense>{group.claims.map((c) => renderClaimRow(c))}</List>
                      </Collapse>
                    </Paper>
                  ))}
                </>
              )}
            </Box>
          )}

          {mainTab === 2 && (
            <>
              {loading && selectedChatId && !conversation ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : conversation ? (
                <Paper variant="outlined" sx={{ p: 2, maxHeight: '65vh', overflow: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    Click a Synapse response (underlined) to open the read-path lens and see what the User Object contributed.
                  </Typography>
                  {conversation.turns.map((turn) => {
                    const readPathClickable = canOpenReadPathLens(turn);
                    return (
                    <Box key={turn.turnId} sx={{ mb: 2, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary">
                        [Turn {turn.turnIndex}] {turn.timestamp}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <strong>User:</strong> {turn.userText}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.5,
                          cursor: readPathClickable ? 'pointer' : 'default',
                          color: readPathClickable ? 'primary.main' : 'text.primary',
                          textDecoration: readPathClickable ? 'underline' : 'none',
                          textUnderlineOffset: 2,
                        }}
                        onClick={() => readPathClickable && openReadPathLens(turn.turnId, turn.turnIndex)}
                        title={
                          readPathClickable
                            ? 'Inspect what the User Object contributed to this response'
                            : undefined
                        }
                      >
                        <strong>Synapse:</strong> {turn.assistantText.slice(0, 500)}
                        {turn.assistantText.length > 500 ? '…' : ''}
                      </Typography>
                      {turn.memoryEvents.map((ev, i) => (
                        <Typography
                          key={i}
                          variant="caption"
                          display="block"
                          sx={{ ml: 2, color: 'primary.main', cursor: ev.claimId ? 'pointer' : 'default' }}
                          onClick={() => ev.claimId && openClaim(ev.claimId)}
                        >
                          ↳{' '}
                          {ev.kind === 'stored'
                            ? `✚ Stored: ${ev.predicate}="${ev.value}" (${ev.dimension}, conf ${ev.confidence?.toFixed(2)})`
                            : ev.kind === 'reinforced'
                              ? `↑ Reinforced: ${ev.predicate}="${ev.value}"`
                              : ev.kind === 'retrieved'
                                ? `⟲ Retrieved: ${ev.predicate}="${ev.value}" (${ev.dimension}, conf ${ev.confidence?.toFixed(2)})`
                                : '→ No new claims'}
                        </Typography>
                      ))}
                    </Box>
                    );
                  })}
                </Paper>
              ) : sessions.length > 0 ? (
                <Alert severity="info">Choose a session above to view the transcript.</Alert>
              ) : null}
            </>
          )}

          {mainTab === 3 && !clientDemoMode && (
            <Stack spacing={2}>
              {conflicts.map((item) => (
                <Paper key={item.conflict.id} variant="outlined" sx={{ p: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      label={item.conflict.status}
                      color={item.conflict.status === 'pending' ? 'warning' : 'default'}
                    />
                    {item.typedAs && (
                      <Chip size="small" variant="outlined" label={CONFLICT_TYPE_LABELS[item.typedAs] ?? item.typedAs} />
                    )}
                    {item.resolutionAction && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color={item.resolutionAction === 'ESCALATE_REVIEW' ? 'warning' : 'default'}
                        label={RESOLUTION_ACTION_LABELS[item.resolutionAction] ?? item.resolutionAction}
                      />
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {item.conflict.reason}
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={6}>
                      <ConflictClaimPanel
                        label="Existing"
                        claim={item.existingClaim}
                        evidence={item.existingEvidence}
                        sourceTurnId={item.existingSourceTurnId}
                        onOpenClaim={openClaim}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <ConflictClaimPanel
                        label="Candidate"
                        claim={item.candidateClaim}
                        evidence={item.candidateEvidence}
                        sourceTurnId={item.candidateSourceTurnId}
                        onOpenClaim={openClaim}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              ))}
              {conflicts.length === 0 && <Alert severity="success">No pending conflicts.</Alert>}
            </Stack>
          )}
        </>
      )}

      <ClaimDetailDrawer
        open={claimDrawerOpen}
        detail={claimDetail}
        userId={selectedUserId}
        accessToken={token}
        clientDemoMode={clientDemoMode}
        onClose={() => setClaimDrawerOpen(false)}
        onOpenClaim={openClaim}
        onViewInConversation={viewClaimInConversation}
        onClaimUpdated={clientDemoMode ? undefined : () => void refreshClaimDetail()}
      />

      <ReadPathLensPanel
        open={readPathOpen}
        loading={readPathLoading}
        error={readPathError}
        lens={readPathLens}
        turnIndex={readPathTurnIndex}
        onClose={() => setReadPathOpen(false)}
        onOpenClaim={(claimId) => {
          setReadPathOpen(false);
          void openClaim(claimId);
        }}
      />

      {!clientDemoMode && (
        <AddManualClaimDialog
          open={addClaimOpen}
          userId={selectedUserId}
          dimension={dimension}
          accessToken={token}
          onClose={() => setAddClaimOpen(false)}
          onAdded={(claimId) => {
            setAddClaimOpen(false);
            void openClaim(claimId);
            if (dimension && selectedUserId) {
              void fetchDimensionView(selectedUserId, dimension, token, requestOptions).then(setDimensionView);
            }
          }}
        />
      )}

      {!clientDemoMode && (
      <Dialog open={bulkRetractOpen} onClose={() => setBulkRetractOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Bulk retract claims</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Retract {selectedClaimIds.size} selected claim(s) with a shared reason.
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Reason</InputLabel>
            <Select
              label="Reason"
              value={bulkRetractReason}
              onChange={(e) => setBulkRetractReason(e.target.value as typeof bulkRetractReason)}
            >
              <MenuItem value="misattribution">Misattribution</MenuItem>
              <MenuItem value="low_quality">Low quality</MenuItem>
              <MenuItem value="privacy_concern">Privacy concern</MenuItem>
              <MenuItem value="study_protocol_exclusion">Study protocol exclusion</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkRetractOpen(false)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={() => {
              void bulkRetractClaims(
                selectedUserId,
                { claimIds: Array.from(selectedClaimIds), reason: bulkRetractReason },
                token,
                requestOptions
              )
                .then(() => {
                  setBulkRetractOpen(false);
                  setSelectedClaimIds(new Set());
                  if (dimension) {
                    return fetchDimensionView(selectedUserId, dimension, token, requestOptions).then(setDimensionView);
                  }
                  return undefined;
                })
                .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Bulk retract failed'));
            }}
          >
            Retract all
          </Button>
        </DialogActions>
      </Dialog>
      )}
    </Box>
  );
};

export default SynapseIntrospectionTab;
